import {
    useState
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    ToggleGroup,
    ScrollArea,
    Chip
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "ScrollArea";

const USAGE_CODE = `<ScrollArea
    style={{
        height: 240
    }}
    aria-label="Tags"
    type="hover"
>
    {tags.map((tag) => <Chip
        title={tag}
        key={tag}
    />)}
</ScrollArea>`;

const HORIZONTAL_CODE = `<ScrollArea
    contentContainerStyle={{
        flexDirection: "row",
        gap: 8
    }}
    orientation="horizontal"
    aria-label="Tags"
>
    {tags.map((tag) => <Chip
        title={tag}
        key={tag}
    />)}
</ScrollArea>`;

const TAG_KEYS = [
    "scrollAreaPage.tag1",
    "scrollAreaPage.tag2",
    "scrollAreaPage.tag3",
    "scrollAreaPage.tag4",
    "scrollAreaPage.tag5",
    "scrollAreaPage.tag6",
    "scrollAreaPage.tag7",
    "scrollAreaPage.tag8",
    "scrollAreaPage.tag9",
    "scrollAreaPage.tag10",
    "scrollAreaPage.tag11",
    "scrollAreaPage.tag12"
];

const TYPES = [
    "hover",
    "scroll",
    "auto",
    "always"
] as const;

type ScrollType = typeof TYPES[number];

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("scrollAreaPage.horizontalTitle"),
        id: "horizontal"
    },
    {
        title: t("scrollAreaPage.typeTitle"),
        id: "types"
    },
    {
        title: t("scrollAreaPage.fadeTitle"),
        id: "fade"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ScrollAreaPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        type,
        setType
    ] = useState<ScrollType>("hover");

    const handleType = (value: string | Array<string>) => {
        if(typeof value === "string" && value) {
            setType(value as ScrollType);
        }
    };

    const tags = TAG_KEYS.map((key, index) => ({
        title: `${t(key)} · ${(index + 2) * 7}`,
        key
    }));

    const rowStyle = {
        flexDirection: "row" as const,
        gap: spaces.spacingSm
    };

    const listStyle = {
        alignItems: "flex-start" as const,
        padding: spaces.spacingSm,
        gap: spaces.spacingSm
    };

    const areaHeight = shapeConfigs.drawerWidth * 0.75;

    const renderVertical = (scrollType: ScrollType, isFadeVisible = true) => <DemoFrame>
        <ScrollArea
            aria-label={t("scrollAreaPage.tagsLabel")}
            contentContainerStyle={listStyle}
            isFadeVisible={isFadeVisible}
            style={{
                height: areaHeight
            }}
            type={scrollType}
        >
            {
                tags.map((tag) => <Chip
                    title={tag.title}
                    key={tag.key}
                />)
            }
        </ScrollArea>
    </DemoFrame>;

    return <Article>
        <ComponentHeader
            lead={t("scrollAreaPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("scrollAreaPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("scrollAreaPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("scrollAreaPage.overviewCaption")}
                isStacked={true}
            >
                <View
                    style={{
                        maxWidth: shapeConfigs.drawerWidth
                    }}
                >
                    {renderVertical("hover")}
                </View>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("scrollAreaPage.horizontalTitle")}
            id="horizontal"
        >
            <Paragraph>
                {t("scrollAreaPage.horizontalText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <View
                    style={{
                        maxWidth: shapeConfigs.drawerWidth
                    }}
                >
                    <DemoFrame>
                        <ScrollArea
                            aria-label={t("scrollAreaPage.tagsLabel")}
                            contentContainerStyle={{
                                ...rowStyle,
                                padding: spaces.spacingSm,
                                paddingBottom: spaces.spacingMd
                            }}
                            orientation="horizontal"
                        >
                            {
                                tags.map((tag) => <Chip
                                    title={tag.title}
                                    key={tag.key}
                                />)
                            }
                        </ScrollArea>
                    </DemoFrame>
                </View>
            </Preview>
            <CodeBlock
                code={HORIZONTAL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("scrollAreaPage.typeTitle")}
            id="types"
        >
            <Paragraph>
                {t("scrollAreaPage.typeText")}
            </Paragraph>
            <Preview
                caption={t("scrollAreaPage.typeCaption", [
                    type
                ])}
                isStacked={true}
            >
                <View
                    style={{
                        maxWidth: shapeConfigs.drawerWidth,
                        gap: spaces.spacingMd
                    }}
                >
                    <ToggleGroup
                        title={t("scrollAreaPage.typeLabel")}
                        attachBehaviour="attached"
                        onValueChange={handleType}
                        variant="outline"
                        value={type}
                    >
                        {
                            TYPES.map((item) => <ToggleGroup.Item
                                title={item}
                                value={item}
                                key={item}
                            />)
                        }
                    </ToggleGroup>
                    {renderVertical(type)}
                </View>
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("scrollAreaPage.nativeNote")}
            </Paragraph>
        </Section>
        <Section
            title={t("scrollAreaPage.fadeTitle")}
            id="fade"
        >
            <Paragraph>
                {t("scrollAreaPage.fadeText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <View
                    style={{
                        maxWidth: shapeConfigs.drawerWidth
                    }}
                >
                    {renderVertical("auto", false)}
                </View>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("scrollAreaPage.a11yFocus"),
                    t("scrollAreaPage.a11yLabel"),
                    t("scrollAreaPage.a11yBar"),
                    t("scrollAreaPage.a11yRtl"),
                    t("scrollAreaPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("scrollAreaPage.propOrientation"),
                        type: "\"vertical\" | \"horizontal\" | \"both\"",
                        defaultValue: "\"vertical\"",
                        name: "orientation"
                    },
                    {
                        type: "\"hover\" | \"scroll\" | \"auto\" | \"always\"",
                        description: t("scrollAreaPage.propType"),
                        defaultValue: "\"hover\"",
                        name: "type"
                    },
                    {
                        description: t("scrollAreaPage.propIsFadeVisible"),
                        name: "isFadeVisible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("scrollAreaPage.propFadeColor"),
                        name: "customFadeColor",
                        type: "string"
                    },
                    {
                        description: t("scrollAreaPage.propContentContainerStyle"),
                        name: "contentContainerStyle",
                        type: "StyleProp<ViewStyle>"
                    },
                    {
                        description: t("scrollAreaPage.propAriaLabel"),
                        name: "aria-label",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("scrollAreaPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("scrollAreaPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("scrollAreaPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ScrollAreaPage;
